/**
 * KPI Card Component
 *
 * Displays a single KPI metric with optional expandable drill-down content.
 * Supports expandable drill-down content for detailed breakdowns.
 *
 * Props:
 * - title: KPI name
 * - description: Brief explanation
 * - value: Numeric value (null shows "N/A")
 * - format: Display format (percent, currency, number, days)
 * - isExpandable/isExpanded: For drill-down functionality
 *
 * Created: 2026-03-28
 * Part of KPI V1 Implementation
 */

interface KpiCardProps {
    title: string;
    description: React.ReactNode;
    value?: number | null;
    displayValueText?: React.ReactNode;
    format: "percent" | "currency" | "number" | "days";
    isLoading?: boolean;
    icon?: string;
    showSign?: boolean;         // Show +/- for growth rates

    // Drill-down props
    isExpandable?: boolean;
    isExpanded?: boolean;
    onToggleExpand?: () => void;
    drillDownContent?: React.ReactNode;
    expandedHeaderAside?: React.ReactNode;
    isInteractive?: boolean;
    isActive?: boolean;
    onClick?: () => void;
    actionLabel?: string;

    // Hover prefetch
    onMouseEnter?: () => void;
}

export default function KpiCard({
    title,
    description,
    value,
    displayValueText,
    format,
    isLoading = false,
    icon = "fa-chart-simple",
    showSign = false,
    isExpandable = false,
    isExpanded = false,
    onToggleExpand,
    drillDownContent,
    expandedHeaderAside,
    isInteractive = false,
    isActive = false,
    onClick,
    actionLabel,
    onMouseEnter,
}: KpiCardProps) {
    /**
     * Format the value based on format type
     */
    const formatValue = (val: number | null | undefined): string => {
        if (val === null || val === undefined) return "N/A";

        switch (format) {
            case "percent":
                const sign = showSign && val > 0 ? "+" : "";
                return `${sign}${val.toFixed(1)}%`;
            case "currency":
                return val.toLocaleString("en-US", {
                    style: "currency",
                    currency: "USD",
                    currencyDisplay: "symbol",
                    maximumFractionDigits: 0,
                });
            case "days":
                return `${val.toFixed(1)} days`;
            default:
                return val.toLocaleString("en-US", { maximumFractionDigits: 2 });
        }
    };

    const handleCardClick = () => {
        if (isExpandable && !isExpanded) {
            onToggleExpand?.();
            return;
        }

        if (isInteractive) {
            onClick?.();
        }
    };

    const isClickable = (isExpandable && !isExpanded) || isInteractive;

    return (
        <div
            className={`rounded-sm border bg-white shadow-default dark:bg-boxdark transition-all duration-300 ${
                isExpanded ? "col-span-full" : ""
            } ${
                isActive
                    ? "border-primary ring-1 ring-primary/30 dark:border-primary"
                    : "border-stroke dark:border-strokedark"
            } ${isClickable ? "hover:-translate-y-0.5 hover:shadow-lg" : ""}`}
            onMouseEnter={onMouseEnter}
        >
            {/* Card Header */}
            <div
                className={`p-6 ${isClickable ? "cursor-pointer hover:bg-gray-50 dark:hover:bg-meta-4" : ""}`}
                onClick={isClickable ? handleCardClick : undefined}
            >
                <div className="flex items-center justify-between">
                    {/* Icon */}
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-meta-2 dark:bg-meta-4">
                        <i className={`fa-light ${icon} fa-lg text-primary`} />
                    </div>

                    {/* Expand / action icon */}
                    <div className="flex items-center gap-2">
                        {isExpandable && (
                            <button
                                type="button"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    onToggleExpand?.();
                                }}
                                className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-stroke text-sm font-semibold text-bodydark2 transition-colors hover:border-primary hover:text-primary dark:border-strokedark"
                                aria-label={isExpanded ? `Collapse ${title}` : `Expand ${title}`}
                            >
                                {isExpanded ? "-" : "+"}
                            </button>
                        )}
                        {!isExpandable && isInteractive && (
                            <i className="fa-light fa-arrow-right text-sm text-bodydark2" />
                        )}
                    </div>
                </div>

                {/* Value, title, and expanded header aside */}
                <div
                    className={`mt-4 ${
                        isExpanded && expandedHeaderAside
                            ? "flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between"
                            : ""
                    }`}
                >
                    <div className="min-w-0">
                        <h4 className="text-xl font-bold text-black dark:text-white">
                            {isLoading ? (
                            <div className="h-7 w-24 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
                            ) : displayValueText ? (
                                displayValueText
                            ) : (
                                formatValue(value)
                            )}
                        </h4>
                        <p className="mt-1 text-sm font-medium text-black dark:text-white">{title}</p>
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{description}</p>
                    </div>

                    {isExpanded && expandedHeaderAside && (
                        <div className="w-full xl:max-w-[360px] xl:flex-shrink-0">{expandedHeaderAside}</div>
                    )}
                </div>

                {isInteractive && actionLabel && (
                    <div className="mt-4 pt-4 border-t border-stroke dark:border-strokedark">
                        <div className="flex items-center justify-between text-xs">
                            <span className="text-primary font-medium">{actionLabel}</span>
                            <i className="fa-light fa-arrow-right text-primary" />
                        </div>
                    </div>
                )}
            </div>

            {/* Expandable drill-down content */}
            {isExpandable && isExpanded && drillDownContent && (
                <div className="border-t border-stroke dark:border-strokedark p-5 bg-gray-50 dark:bg-meta-4">
                    {drillDownContent}
                </div>
            )}
        </div>
    );
}
